import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LogOut, CalendarClock, Mail, Inbox, Phone, Trash2, Dog, Droplets, Euro, CalendarDays,
  RefreshCw, Search, BellRing, Bell, BellOff, Leaf, Pencil, CheckCircle2, Clock3, MailCheck,
  MailX, Send, AlarmClock, AlarmClockCheck, MessageSquare, AlertCircle, BarChart3, Users,
  Images, Settings2, ListChecks, PhoneCall, LayoutDashboard, FileText, Activity, Menu, X, Scissors, Megaphone, Ticket, ShoppingBag, Sparkles, Palette, Star, Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LOGO_URL } from "@/i18n/translations";
import { startAlarm, stopAlarm, primeAudio } from "@/lib/alarm";
import api from "@/lib/api";
import EditBookingDialog from "@/components/admin/EditBookingDialog";
import GroomingSlotsDialog from "@/components/admin/GroomingSlotsDialog";
import PhoneBookingDialog from "@/components/admin/PhoneBookingDialog";
import ReminderScheduleDialog from "@/components/admin/ReminderScheduleDialog";
import MonthCalendar from "@/components/admin/MonthCalendar";
import { ManualReminderButton } from "@/components/admin/ManualReminderButton";
import { ReviewPromptDialog } from "@/components/admin/ReviewPromptDialog";
import { WinbackPanel } from "@/components/admin/WinbackPanel";
import { DevShop } from "@/components/admin/DevShop";
import { DevPopup } from "@/components/admin/DevPopup";
import { ThemePanel } from "@/components/admin/ThemePanel";
import { ReviewsPanel } from "@/components/admin/ReviewsPanel";
import { AppUsersPanel, AppMobilePanel } from "@/components/admin/AppPanels";
import {
  DevAnalytics, DevCustomers, DevGallery, DevMessaging, DevSettings, DevAllBookings, DevTemplates, DevSystem, DevAnnouncements,
} from "@/components/admin/DevPanels";

// ---- shared tokens (same look & feel as the shop dashboard) ---------------
const lightCard = "rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]";
const lightInput = "rounded-full border-brand/20 bg-white h-11 focus-visible:ring-brand";
const acc = "text-brand";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  done: "bg-brand-soft text-brand",
  cancelled: "bg-rose-100 text-rose-800",
  no_show: "bg-brand-soft/50 text-brand-ink/60",
};

const StatusBadge = ({ status, labels }) => (
  <Badge className={`${STATUS_COLORS[status] || ""} border-0 font-bold`}>{labels[status] || status}</Badge>
);

const StatusSelect = ({ value, onChange, options, labels, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-[150px] rounded-full border-brand/20 bg-stone-alt/50 text-brand-ink h-9 text-sm"><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((k) => <SelectItem key={k} value={k}>{labels[k]}</SelectItem>)}
    </SelectContent>
  </Select>
);

const EmailBadge = ({ booking, type, onSend, labels }) => {
  if (!booking.email) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft/50 text-brand-ink/50 text-[11px] font-bold px-2.5 py-0.5" data-testid={`email-none-${booking.id}`}>
        <MailX className="h-3 w-3" /> {labels.email_none}
      </span>
    );
  }
  if (booking.email_sent) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`email-sent-${booking.id}`}>
        <MailCheck className="h-3 w-3" /> {labels.email_sent_badge}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onSend(type, booking.id)}
      data-testid={`send-email-${booking.id}`}
      className="inline-flex items-center gap-1 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-[11px] font-bold px-2.5 py-0.5 transition-colors"
      title={labels.email_send_title}
    >
      <Send className="h-3 w-3" /> {labels.email_not_sent_badge}
    </button>
  );
};

const ReminderBadge = ({ booking, type, onSend, onEdit, labels, lang }) => {
  if (!booking.email) return null;
  if (booking.reminder_sent) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`reminder-sent-${booking.id}`}>
        <AlarmClockCheck className="h-3 w-3" /> {labels.reminder_sent_badge}
      </span>
    );
  }
  const when = booking.reminder_scheduled_at
    ? new Date(booking.reminder_scheduled_at).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" })
    : labels.reminder_not_scheduled;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft text-brand text-[11px] font-bold px-2 py-0.5" data-testid={`reminder-pending-${booking.id}`}>
      <AlarmClock className="h-3 w-3" />
      <span>{labels.reminder_at}: {when}</span>
      <button type="button" onClick={() => onSend(type, booking.id)} title={labels.reminder_send_title}
        className="ml-1 rounded-full bg-brand text-brand-cream text-[10px] px-2 py-0.5 hover:bg-brand-hover" data-testid={`send-reminder-${booking.id}`}>
        {labels.reminder_send_now}
      </button>
      <button type="button" onClick={() => onEdit(type, booking)} title={labels.reminder_edit_title}
        className="rounded-full bg-brand-soft/50 text-brand text-[10px] px-2 py-0.5 hover:bg-brand-soft border border-brand/20" data-testid={`edit-reminder-${booking.id}`}>
        <Pencil className="h-3 w-3 inline" />
      </button>
    </span>
  );
};

const SmsBadge = ({ booking, type, onResend, labels, lang }) => {
  const st = booking.sms_status;
  if (!st) return null;
  if (st === "sent") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`sms-sent-${booking.id}`}>
        <MessageSquare className="h-3 w-3" /> {labels.sms.badge_sent}
      </span>
    );
  }
  if (st === "failed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 text-[11px] font-bold px-2 py-0.5" title={booking.sms_error || ""} data-testid={`sms-failed-${booking.id}`}>
        <AlertCircle className="h-3 w-3" />
        <span>{labels.sms.badge_failed}</span>
        <button type="button" onClick={() => onResend(type, booking.id)}
          className="ml-1 rounded-full bg-rose-500 text-white text-[10px] px-2 py-0.5 hover:bg-rose-600" data-testid={`resend-sms-${booking.id}`}>
          {labels.sms.resend}
        </button>
      </span>
    );
  }
  const when = booking.sms_scheduled_at
    ? new Date(booking.sms_scheduled_at).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" })
    : null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-700 text-[11px] font-bold px-2 py-0.5" data-testid={`sms-pending-${booking.id}`}>
      <MessageSquare className="h-3 w-3" />
      <span>{labels.sms.badge_pending}{when ? `: ${when}` : ""}</span>
    </span>
  );
};

export default function DevConsole() {
  const { user, ready, logout } = useAuth();
  const { t, lang } = useLang();
  const a = t.admin;
  const d = t.admin.dev;
  const navigate = useNavigate();

  const [section, setSection] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [selfwash, setSelfwash] = useState([]);
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [alertActive, setAlertActive] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [editState, setEditState] = useState({ open: false, item: null, type: null });
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [phoneBookOpen, setPhoneBookOpen] = useState(false);
  const [reminderState, setReminderState] = useState({ open: false, type: null, booking: null });
  const [reviewPrompt, setReviewPrompt] = useState({ open: false, type: null, booking: null });
  const [selectedDay, setSelectedDay] = useState(null);
  const seenRef = useRef(null);
  const soundOnRef = useRef(true);

  const openEdit = (item, type) => setEditState({ open: true, item, type });

  const sendConfirmation = async (type, id) => {
    try { await api.post(`/admin/bookings/${type}/${id}/send-confirmation`); toast.success(a.email_sent_toast); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || a.email_send_error); }
  };
  const sendReminder = async (type, id) => {
    if (!window.confirm(a.reminder_confirm_send)) return;
    try { await api.post(`/admin/bookings/${type}/${id}/send-reminder`); toast.success(a.reminder_sent_toast); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || a.email_send_error); }
  };
  const editReminderSchedule = (type, booking) => setReminderState({ open: true, type, booking });
  const resendSms = async (type, id) => {
    try { await api.post(`/admin/bookings/${type}/${id}/resend-sms`); toast.success(a.sms.resent_toast); }
    catch (err) { toast.error(err.response?.data?.detail || a.sms.send_error); }
    await load();
  };

  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);
  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);

  useEffect(() => {
    const onGesture = () => primeAudio();
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    return () => { window.removeEventListener("pointerdown", onGesture); window.removeEventListener("keydown", onGesture); };
  }, []);

  const detectNew = useCallback((groomingList, selfwashList) => {
    const currentIds = [
      ...groomingList.filter((g) => g.status === "confirmed" || g.status === "pending").map((g) => `g:${g.id}`),
      ...selfwashList.map((s) => `s:${s.id}`),
    ];
    if (seenRef.current === null) { seenRef.current = new Set(currentIds); return; }
    const fresh = currentIds.filter((id) => !seenRef.current.has(id));
    currentIds.forEach((id) => seenRef.current.add(id));
    if (fresh.length > 0) { setAlertActive(true); if (soundOnRef.current) startAlarm(); }
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [b, sw, m] = await Promise.all([
        api.get("/admin/grooming-requests"),
        api.get("/admin/selfwash-bookings"),
        api.get("/admin/contact-messages"),
      ]);
      setBookings(b.data);
      setSelfwash(sw.data);
      setMessages(m.data);
      detectNew(b.data, sw.data);
    } catch { toast.error("Error loading data"); }
    finally { setRefreshing(false); }
  }, [detectNew]);

  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => {
    if (!user) return;
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [user, load]);
  useEffect(() => () => stopAlarm(), []);

  const acknowledgeAlert = () => { stopAlarm(); setAlertActive(false); };
  const toggleSound = () => setSoundOn((v) => { const next = !v; if (!next) stopAlarm(); return next; });

  const changeBookingStatus = async (id, status) => {
    await api.patch(`/admin/grooming-requests/${id}`, { status });
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    if (status === "done") {
      const b = bookings.find((x) => x.id === id);
      if (b?.phone) setReviewPrompt({ open: true, type: "grooming", booking: b });
    }
    load();
  };
  const changeSwStatus = async (id, status) => {
    await api.patch(`/admin/selfwash-bookings/${id}`, { status });
    setSelfwash((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    if (status === "done") {
      const b = selfwash.find((x) => x.id === id);
      if (b?.phone) setReviewPrompt({ open: true, type: "selfwash", booking: b });
    }
    load();
  };
  const delBooking = async (id) => { await api.delete(`/admin/grooming-requests/${id}`); setBookings((p) => p.filter((b) => b.id !== id)); load(); };
  const delSw = async (id) => { await api.delete(`/admin/selfwash-bookings/${id}`); setSelfwash((p) => p.filter((b) => b.id !== id)); load(); };
  const delMessage = async (id) => { await api.delete(`/admin/contact-messages/${id}`); setMessages((p) => p.filter((m) => m.id !== id)); load(); };

  const fmt = (iso) => new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "medium", timeStyle: "short" });
  const q = query.trim().toLowerCase();
  const match = (str) => !q || (str || "").toLowerCase().includes(q);

  const ARCHIVED = new Set(["done", "cancelled", "no_show"]);
  const isArchived = (b) => ARCHIVED.has(b.status);
  const activeBookings = bookings.filter((b) => !isArchived(b));
  const activeSelfwash = selfwash.filter((b) => !isArchived(b));
  const completedItems = useMemo(() => {
    const g = bookings.filter(isArchived).map((b) => ({ ...b, _type: "grooming", _dt: b.preferred_date, _tm: b.preferred_time }));
    const s = selfwash.filter(isArchived).map((b) => ({ ...b, _type: "selfwash", _dt: b.date, _tm: b.time }));
    return [...g, ...s].sort((x, y) => (y.created_at || "").localeCompare(x.created_at || ""));
  }, [bookings, selfwash]);

  const fBookings = useMemo(() => activeBookings.filter((b) => match(b.customer_name) || match(b.phone) || match(b.pet_name)), [activeBookings, q]);
  const fSelfwash = useMemo(() => activeSelfwash.filter((b) => match(b.customer_name) || match(b.phone)), [activeSelfwash, q]);
  const fCompleted = useMemo(() => completedItems.filter((b) => match(b.customer_name) || match(b.phone) || match(b.pet_name)), [completedItems, q]);
  const fMessages = useMemo(() => messages.filter((m) => match(m.first_name) || match(m.last_name) || match(m.email) || match(m.message)), [messages, q]);

  const todayStr = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; })();
  const todayItems = useMemo(() => {
    const g = bookings.filter((b) => b.preferred_date === todayStr && b.status === "confirmed").map((b) => ({ ...b, _type: "grooming", _tm: b.preferred_time }));
    const s = selfwash.filter((b) => b.date === todayStr && b.status === "confirmed").map((b) => ({ ...b, _type: "selfwash", _tm: b.time }));
    return [...g, ...s].sort((x, y) => (x._tm || "").localeCompare(y._tm || ""));
  }, [bookings, selfwash, todayStr]);

  const weekItems = useMemo(() => {
    const end = new Date(); end.setDate(end.getDate() + 7);
    const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
    const inRange = (ds) => ds && ds >= todayStr && ds <= endStr;
    return bookings.filter((b) => inRange(b.preferred_date) && b.status === "confirmed").length
      + selfwash.filter((b) => inRange(b.date) && b.status === "confirmed").length;
  }, [bookings, selfwash, todayStr]);

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  const dayGrooming = useMemo(() => (selectedDay ? bookings.filter((b) => b.preferred_date === selectedDay && b.status !== "cancelled") : []), [selectedDay, bookings]);
  const daySelfwash = useMemo(() => (selectedDay ? selfwash.filter((b) => b.date === selectedDay && b.status !== "cancelled") : []), [selectedDay, selfwash]);

  // ---- booking rows -------------------------------------------------------
  const renderGroomingRow = (b) => (
    <div key={b.id} className={`rounded-2xl border p-5 ${b.coupon_code ? "bg-emerald-50/70 border-emerald-300" : "bg-white border-brand/10"}`} data-testid={`booking-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-brand-ink">{b.customer_name}</h3>
            <StatusBadge status={b.status} labels={a.statuses} />
            {b.coupon_code && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5" data-testid={`coupon-badge-${b.id}`}>
                <Ticket className="h-3 w-3" /> {a.discount_applied} {b.coupon_discount} · {b.coupon_code}
              </span>
            )}
            <EmailBadge booking={b} type="grooming" onSend={sendConfirmation} labels={a} />
            <ReminderBadge booking={b} type="grooming" onSend={sendReminder} onEdit={editReminderSchedule} labels={a} lang={lang} />
            <SmsBadge booking={b} type="grooming" onResend={resendSms} labels={a} lang={lang} />
            <ManualReminderButton booking={b} type="grooming" onDone={load} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-ink/70">
            <span className="flex items-center gap-1.5"><Phone className={`h-4 w-4 ${acc}`} /> {b.phone}</span>
            {b.email && <span className="flex items-center gap-1.5"><Mail className={`h-4 w-4 ${acc}`} /> {b.email}</span>}
            <span className="flex items-center gap-1.5"><Dog className={`h-4 w-4 ${acc}`} /> {b.pet_name}{b.pet_breed ? ` · ${b.pet_breed}` : ""} ({t.grooming.sizes[b.pet_size] || b.pet_size})</span>
            <span className="flex items-center gap-1.5 font-mono"><CalendarClock className={`h-4 w-4 ${acc}`} /> {b.preferred_date} · {b.preferred_time}</span>
          </div>
          {b.service && <p className="mt-2 text-sm text-brand-ink/70"><span className="font-semibold">Service:</span> {b.service}</p>}
          {b.notes && <p className="mt-1 text-sm text-brand-ink/50 italic">{`"${b.notes}"`}</p>}
          <p className="mt-2 text-xs text-brand-ink/40 font-mono">{fmt(b.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect value={b.status} onChange={(v) => changeBookingStatus(b.id, v)} options={["confirmed", "done", "cancelled"]} labels={a.statuses} testid={`status-select-${b.id}`} />
          <Button data-testid={`edit-booking-${b.id}`} onClick={() => openEdit(b, "grooming")} variant="ghost" size="icon" className={`rounded-full ${acc} hover:bg-brand-soft/50`}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button data-testid={`delete-booking-${b.id}`} onClick={() => delBooking(b.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSelfwashRow = (b) => (
    <div key={b.id} className={`rounded-2xl border p-5 ${b.coupon_code ? "bg-emerald-50/70 border-emerald-300" : "bg-white border-brand/10"}`} data-testid={`selfwash-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-brand-ink">{b.customer_name}</h3>
            <StatusBadge status={b.status} labels={a.statuses} />
            {b.coupon_code && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5" data-testid={`coupon-badge-${b.id}`}>
                <Ticket className="h-3 w-3" /> {a.discount_applied} {b.coupon_discount} · {b.coupon_code}
              </span>
            )}
            {b.nagayu && <span className="rounded-full bg-bamboo-soft text-bamboo-deep text-xs font-bold px-2.5 py-0.5 inline-flex items-center gap-1"><Leaf className="h-3 w-3" /> Nagayu</span>}
            <span className="rounded-full bg-brand-soft text-brand text-xs font-bold px-2.5 py-0.5">€{b.total_price || b.plan_price}</span>
            <EmailBadge booking={b} type="selfwash" onSend={sendConfirmation} labels={a} />
            <ReminderBadge booking={b} type="selfwash" onSend={sendReminder} onEdit={editReminderSchedule} labels={a} lang={lang} />
            <SmsBadge booking={b} type="selfwash" onResend={resendSms} labels={a} lang={lang} />
            <ManualReminderButton booking={b} type="selfwash" onDone={load} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-ink/70">
            <span className="flex items-center gap-1.5"><Phone className={`h-4 w-4 ${acc}`} /> {b.phone}</span>
            {b.email && <span className="flex items-center gap-1.5"><Mail className={`h-4 w-4 ${acc}`} /> {b.email}</span>}
            <span className="flex items-center gap-1.5"><Droplets className={`h-4 w-4 ${acc}`} /> {t.grooming.sizes[b.plan] || b.plan} · {b.plan_duration}′</span>
            <span className="flex items-center gap-1.5 font-mono"><CalendarClock className={`h-4 w-4 ${acc}`} /> {b.date} · {b.time}</span>
          </div>
          {b.notes && <p className="mt-1 text-sm text-brand-ink/50 italic">{`"${b.notes}"`}</p>}
          <p className="mt-2 text-xs text-brand-ink/40 font-mono">{fmt(b.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect value={b.status} onChange={(v) => changeSwStatus(b.id, v)} options={["confirmed", "done", "no_show", "cancelled"]} labels={a.statuses} testid={`sw-status-select-${b.id}`} />
          <Button data-testid={`edit-selfwash-${b.id}`} onClick={() => openEdit(b, "selfwash")} variant="ghost" size="icon" className={`rounded-full ${acc} hover:bg-brand-soft/50`}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button data-testid={`delete-selfwash-${b.id}`} onClick={() => delSw(b.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const SECTIONS = [
    { id: "overview", label: d.nav_overview, icon: LayoutDashboard },
    { id: "shop", label: t.ashop.nav_boutique, icon: ShoppingBag },
    { id: "grooming", label: a.bookings, icon: Scissors, count: activeBookings.length },
    { id: "selfwash", label: a.selfwash, icon: Droplets, count: activeSelfwash.length },
    { id: "completed", label: a.completed, icon: CheckCircle2, count: completedItems.length },
    { id: "messages", label: a.messages, icon: Inbox, count: messages.length },
    { id: "all", label: a.tab_all, icon: ListChecks },
    { id: "analytics", label: a.tab_analytics, icon: BarChart3 },
    { id: "customers", label: a.tab_customers, icon: Users },
    { id: "gallery", label: a.tab_gallery, icon: Images },
    { id: "reviews", label: d.nav_reviews, icon: Star },
    { id: "appusers", label: d.nav_app_users, icon: Smartphone },
    { id: "appmobile", label: d.nav_app_mobile, icon: Smartphone },
    { id: "messaging", label: a.tab_messaging, icon: MessageSquare },
    { id: "announcement", label: d.nav_announcement, icon: Megaphone },
    { id: "popup", label: t.popup.title, icon: Sparkles },
    { id: "themes", label: t.themes.nav, icon: Palette },
    { id: "templates", label: d.nav_templates, icon: FileText },
    { id: "settings", label: a.tab_settings, icon: Settings2 },
    { id: "system", label: d.nav_system, icon: Activity },
  ];

  if (!ready || !user) return null;

  const nav = (
    <nav className="flex flex-col gap-1 p-3" data-testid="dev-nav">
      {SECTIONS.map((s) => {
        const Icon = s.icon;
        const active = section === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => { setSection(s.id); setMenuOpen(false); }}
            className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-bold transition-colors text-left ${
              active ? "bg-brand text-brand-cream shadow-sm" : "text-brand-ink/60 hover:bg-brand-soft/40 hover:text-brand-ink"
            }`}
            data-testid={`nav-${s.id}`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{s.label}</span>
            {typeof s.count === "number" && s.count > 0 && (
              <span className={`ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full ${active ? "bg-brand text-brand-cream" : "bg-brand-soft/50 text-brand-ink/60"}`}>{s.count}</span>
            )}
          </button>
        );
      })}
    </nav>
  );

  const sectionTitle = SECTIONS.find((s) => s.id === section)?.label;

  return (
    <div className="min-h-screen bg-brand-soft/20" data-testid="dev-console">
      {/* top bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-brand/10">
        <div className="px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-3">
          <button type="button" className="lg:hidden grid place-items-center h-9 w-9 rounded-lg bg-brand-soft/50 text-brand-ink" onClick={() => setMenuOpen(true)} data-testid="dev-menu-btn" aria-label={d.menu_open}>
            <Menu className="h-5 w-5" />
          </button>
          <img src={LOGO_URL} alt="Care For Me" className="h-6 sm:h-7 w-auto" />
          <span className="font-mono text-[10px] font-bold tracking-[0.15em] px-2 py-1 rounded bg-amber-100 text-amber-700 border border-amber-300 whitespace-nowrap" data-testid="dev-env-badge"><span className="hidden sm:inline">{d.dev_badge}</span><span className="sm:hidden">DEV</span></span>
          <span className="hidden md:inline font-mono text-xs text-brand-ink/40">/ {sectionTitle}</span>
          <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0">
            <Button data-testid="admin-sound-toggle" onClick={toggleSound} variant="ghost" size="icon" title={soundOn ? a.sound_on : a.sound_off} className={`rounded-full ${soundOn ? "text-bamboo-deep" : "text-brand-ink/40"} hover:bg-brand-soft/50`}>
              {soundOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            </Button>
            <Button data-testid="admin-refresh-btn" onClick={load} variant="ghost" size="icon" title={d.refresh} className={`rounded-full ${acc} hover:bg-brand-soft/50`}>
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }} variant="ghost" className={`rounded-full ${acc} hover:bg-brand-soft/50 font-bold px-2.5 sm:px-4`}>
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">{d.logout}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* sidebar (desktop) */}
        <aside className="hidden lg:block w-60 shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto bg-white border-r border-brand/10">
          {nav}
        </aside>
        {/* sidebar (mobile drawer) */}
        {menuOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden" data-testid="dev-mobile-menu">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-64 bg-white border-r border-brand/10 overflow-y-auto">
              <div className="flex items-center justify-between p-3 border-b border-brand/10">
                <span className="font-mono text-[10px] font-bold tracking-[0.15em] px-2 py-1 rounded bg-amber-100 text-amber-800">{d.dev_badge}</span>
                <button type="button" onClick={() => setMenuOpen(false)} className="grid place-items-center h-9 w-9 rounded-lg bg-brand-soft/50 text-brand-ink" data-testid="dev-menu-close" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {nav}
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 px-4 sm:px-6 py-6 max-w-[1200px]">
          {/* search */}
          {["grooming", "selfwash", "completed", "messages"].includes(section) && (
            <div className="mb-5 relative max-w-sm">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink/40" />
              <Input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={a.search} className={`pl-10 ${lightInput}`} />
            </div>
          )}

          {section === "overview" && (
            <div className="space-y-5" data-testid="dev-overview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={lightCard} data-testid="ov-today"><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{d.ov_today}</p><p className="font-heading text-3xl font-bold text-brand-ink mt-1">{todayItems.length}</p></div>
                <div className={lightCard} data-testid="ov-pending"><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{d.ov_pending}</p><p className="font-heading text-3xl font-bold text-amber-700 mt-1">{pendingCount}</p></div>
                <div className={lightCard} data-testid="ov-week"><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{d.ov_week}</p><p className="font-heading text-3xl font-bold text-brand-ink mt-1">{weekItems}</p></div>
                <div className={lightCard} data-testid="ov-messages"><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{d.ov_messages}</p><p className="font-heading text-3xl font-bold text-brand-ink mt-1">{messages.length}</p></div>
              </div>

              <WinbackPanel isDev />

              <div className={lightCard} data-testid="dev-quick-actions">
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 mb-3">{d.quick_actions}</p>
                <div className="flex flex-wrap gap-3">
                  <a href="/landing/grooming" target="_blank" rel="noreferrer" data-testid="btn-landing-grooming">
                    <Button className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold no-a11y-underline"><Dog className="h-4 w-4 mr-2" /> {a.landing_grooming}</Button>
                  </a>
                  <a href="/landing/self" target="_blank" rel="noreferrer" data-testid="btn-landing-self">
                    <Button className="rounded-full bg-bamboo hover:bg-bamboo-hover text-brand-cream font-bold no-a11y-underline"><Droplets className="h-4 w-4 mr-2" /> {a.landing_self}</Button>
                  </a>
                  <Button onClick={() => setSlotsOpen(true)} variant="outline" className={`rounded-full border-brand/30 ${acc} hover:bg-brand-soft/50 font-bold`} data-testid="btn-manage-slots">
                    <Clock3 className="h-4 w-4 mr-2" /> {a.slots.open_button}
                  </Button>
                  <Button onClick={() => setPhoneBookOpen(true)} className="rounded-full bg-brand-soft/50 hover:bg-brand-soft text-brand-ink font-bold" data-testid="btn-phone-booking">
                    <Phone className="h-4 w-4 mr-2" /> {a.phone_book.open_button}
                  </Button>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-[1fr_380px]">
                <MonthCalendar grooming={bookings} selfwash={selfwash} selectedDate={selectedDay} onDaySelect={(dStr) => setSelectedDay(dStr)} />
                <div className={lightCard} data-testid="today-agenda">
                  <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2">
                    <CalendarDays className={`h-5 w-5 ${acc}`} /> {a.today_agenda}
                    <span className="ml-auto rounded-full bg-brand text-brand-cream text-sm font-bold px-3 py-0.5" data-testid="today-count">{todayItems.length}</span>
                  </p>
                  <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
                    {todayItems.length === 0 ? (
                      <p className="text-sm text-brand-ink/40 italic py-4 text-center" data-testid="today-empty">{a.no_today}</p>
                    ) : todayItems.map((b) => (
                      <div key={`${b._type}-${b.id}`} className="flex items-center gap-2 rounded-xl bg-stone-alt/50 px-3 py-2" data-testid={`today-item-${b.id}`}>
                        <span className={`grid place-items-center h-7 w-7 rounded-lg ${b._type === "grooming" ? "bg-brand-soft text-brand" : "bg-bamboo-soft text-bamboo-deep"}`}>
                          {b._type === "grooming" ? <Dog className="h-3.5 w-3.5" /> : <Droplets className="h-3.5 w-3.5" />}
                        </span>
                        <span className="text-sm font-bold text-brand-ink font-mono">{b._tm}</span>
                        <span className="text-sm text-brand-ink/80 truncate">{b.customer_name}{b.pet_name ? ` (${b.pet_name})` : ""}</span>
                        <a href={`tel:${b.phone}`} className="ml-auto grid place-items-center h-8 w-8 rounded-full bg-bamboo text-white hover:bg-bamboo-hover no-a11y-underline" data-testid={`today-call-${b.id}`} aria-label="Call">
                          <PhoneCall className="h-4 w-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {section === "grooming" && (
            fBookings.length === 0 ? <p className="text-center text-brand-ink/40 py-16" data-testid="no-bookings">{a.no_bookings}</p>
              : <div className="grid gap-4" data-testid="bookings-list">{fBookings.map(renderGroomingRow)}</div>
          )}
          {section === "selfwash" && (
            fSelfwash.length === 0 ? <p className="text-center text-brand-ink/40 py-16" data-testid="no-selfwash">{a.no_selfwash}</p>
              : <div className="grid gap-4" data-testid="selfwash-list">{fSelfwash.map(renderSelfwashRow)}</div>
          )}
          {section === "completed" && (
            fCompleted.length === 0 ? <p className="text-center text-brand-ink/40 py-16" data-testid="no-completed">{a.no_completed}</p>
              : (
                <div className="grid gap-4" data-testid="completed-list">
                  {fCompleted.map((b) => (
                    <div key={`${b._type}-${b.id}`} className="rounded-2xl bg-white/70 border border-brand/10 p-5" data-testid={`completed-${b._type}-${b.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[11px] uppercase tracking-wide font-bold px-2.5 py-0.5 rounded-full ${b._type === "grooming" ? "bg-brand-soft text-brand" : "bg-bamboo-soft text-bamboo-deep"}`}>
                              {b._type === "grooming" ? a.grooming_type : a.selfwash_type}
                            </span>
                            <h3 className="font-heading text-lg font-bold text-brand-ink">{b.customer_name}</h3>
                            <StatusBadge status={b.status} labels={a.statuses} />
                            {b.nagayu && <span className="rounded-full bg-bamboo-soft text-bamboo-deep text-xs font-bold px-2.5 py-0.5 inline-flex items-center gap-1"><Leaf className="h-3 w-3" /> Nagayu</span>}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-ink/70">
                            <span className="flex items-center gap-1.5"><Phone className={`h-4 w-4 ${acc}`} /> {b.phone}</span>
                            <span className="flex items-center gap-1.5 font-mono"><CalendarClock className={`h-4 w-4 ${acc}`} /> {b._dt} · {b._tm}</span>
                            {b._type === "grooming" && b.pet_name && <span className="flex items-center gap-1.5"><Dog className={`h-4 w-4 ${acc}`} /> {b.pet_name}</span>}
                            {b._type === "selfwash" && <span className="flex items-center gap-1.5"><Euro className={`h-4 w-4 ${acc}`} /> €{b.total_price || b.plan_price}</span>}
                          </div>
                          {b.notes && <p className="mt-1 text-sm text-brand-ink/50 italic">{`"${b.notes}"`}</p>}
                          <p className="mt-2 text-xs text-brand-ink/40 font-mono">{fmt(b.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-soft text-brand"><CheckCircle2 className="h-4 w-4" /></span>
                          <Button data-testid={`del-completed-${b._type}-${b.id}`} onClick={() => b._type === "grooming" ? delBooking(b.id) : delSw(b.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}
          {section === "messages" && (
            fMessages.length === 0 ? <p className="text-center text-brand-ink/40 py-16" data-testid="no-messages">{a.no_messages}</p>
              : (
                <div className="grid gap-4 md:grid-cols-2" data-testid="messages-list">
                  {fMessages.map((m) => (
                    <div key={m.id} className={lightCard} data-testid={`message-${m.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading text-lg font-bold text-brand-ink">{m.first_name} {m.last_name || ""}</h3>
                          <a href={`mailto:${m.email}`} className={`text-sm ${acc} font-semibold`}>{m.email}</a>
                        </div>
                        <Button data-testid={`delete-message-${m.id}`} onClick={() => delMessage(m.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">{m.message}</p>
                      <p className="mt-3 text-xs text-brand-ink/40 font-mono">{fmt(m.created_at)}</p>
                    </div>
                  ))}
                </div>
              )
          )}

          {section === "all" && <DevAllBookings grooming={bookings} selfwash={selfwash} />}
          {section === "shop" && <DevShop />}
          {section === "popup" && <DevPopup />}
          {section === "themes" && <ThemePanel />}
          {section === "analytics" && <DevAnalytics />}
          {section === "customers" && (
            <div className="space-y-5">
              <WinbackPanel isDev />
              <DevCustomers canSend={true} />
            </div>
          )}
          {section === "gallery" && <DevGallery />}
          {section === "reviews" && <ReviewsPanel />}
          {section === "appusers" && <AppUsersPanel />}
          {section === "appmobile" && <AppMobilePanel />}
          {section === "messaging" && <DevMessaging />}
          {section === "announcement" && <DevAnnouncements />}
          {section === "templates" && <DevTemplates />}
          {section === "settings" && <DevSettings />}
          {section === "system" && <DevSystem />}
        </main>
      </div>

      {alertActive && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm p-5" data-testid="new-booking-alert">
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: [1, 1.03, 1], opacity: 1 }}
            transition={{ scale: { repeat: Infinity, duration: 1 }, opacity: { duration: 0.2 } }}
            className="bg-white rounded-[2rem] border-2 border-brand shadow-2xl p-8 max-w-sm w-full text-center"
          >
            <span className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-brand text-brand-cream mb-4 animate-sway">
              <BellRing className="h-8 w-8" />
            </span>
            <h2 className="font-heading text-3xl font-medium text-brand-ink">{a.new_booking_title}</h2>
            <p className="mt-2 text-brand-ink/60">{a.new_booking_body}</p>
            <Button data-testid="accept-booking-btn" onClick={acknowledgeAlert} className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base">
              {a.accept}
            </Button>
          </motion.div>
        </div>
      )}

      <EditBookingDialog
        open={editState.open}
        onOpenChange={(v) => setEditState((s) => ({ ...s, open: v }))}
        item={editState.item}
        type={editState.type}
        onSaved={(updated) => {
          if (editState.type === "grooming") setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
          else setSelfwash((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
          load();
        }}
      />
      <GroomingSlotsDialog open={slotsOpen} onOpenChange={setSlotsOpen} />
      <PhoneBookingDialog open={phoneBookOpen} onOpenChange={setPhoneBookOpen} onCreated={load} />
      <ReminderScheduleDialog
        open={reminderState.open}
        onOpenChange={(v) => setReminderState((s) => ({ ...s, open: v }))}
        booking={reminderState.booking}
        type={reminderState.type}
        onSaved={load}
      />
      <ReviewPromptDialog
        state={reviewPrompt}
        onClose={(sent) => { setReviewPrompt({ open: false, type: null, booking: null }); if (sent) load(); }}
      />

      <Dialog open={!!selectedDay} onOpenChange={(v) => !v && setSelectedDay(null)}>
        <DialogContent className="bg-white border-brand/10 max-w-2xl rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="day-bookings-dialog">
          <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
                <CalendarDays className="h-5 w-5" /> {a.day_bookings} · {selectedDay}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-6 space-y-4">
            {dayGrooming.length === 0 && daySelfwash.length === 0 ? (
              <p className="text-center text-brand-ink/40 py-8 italic" data-testid="day-empty">{a.no_bookings}</p>
            ) : (
              <>{dayGrooming.map(renderGroomingRow)}{daySelfwash.map(renderSelfwashRow)}</>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
